import axios from "axios";

const FILE_EXTENSIONS = [
  ".jpg",
  ".jpeg",
  ".png",
  ".mp4",
  ".mov",
  ".avi",
  ".mpeg",
  ".quicktime",
];

function getMessageString(value) {
  return typeof value === "string" ? value : "";
}

function stripQuery(url) {
  return getMessageString(url).split("?")[0];
}

function isFileMessage(message) {
  const raw = stripQuery(message);
  if (!raw || raw.indexOf("http") !== 0) return false;

  return FILE_EXTENSIONS.some((ext) => {
    return raw.toLowerCase().endsWith(ext);
  });
}

function normalizeIncomingMessage(msg) {
  if (!msg) return null;

  const message = getMessageString(msg.message);

  if (isFileMessage(message)) {
    return {
      reply_id: msg.reply_id,
      id: msg.id,
      created_at: msg.created_at,
      type: "file",
      author: msg.from == "会员" ? "me" : msg.from,
      data: {
        file: {
          name: "档案",
          url: `${message}`,
        },
      },
    };
  }

  return {
    reply_id: msg.reply_id,
    id: msg.id,
    created_at: msg.created_at,
    type: "text",
    author: msg.from == "会员" ? "me" : msg.from,
    meta: "red",
    data: {
      text: message,
    },
  };
}

function normalizeResponseMessage(msg) {
  if (!msg || !msg.response) return null;

  if (msg.type == "text") {
    const data = {
      text: msg.response,
    };

    if (msg.response_http == 400) {
      data.meta = "發送失敗";
    }

    return {
      reply_id: msg.reply_id,
      created_at: msg.created_at,
      id: msg.id,
      type: msg.type,
      author: "self",
      data,
    };
  }

  const data = {
    file: {
      url: `${msg.response}`,
    },
  };

  if (msg.response_http == 400) {
    data.file.name = "發送失敗";
  }

  return {
    reply_id: msg.reply_id,
    created_at: msg.created_at,
    id: msg.id,
    type: "file",
    author: "self",
    data,
  };
}

function normalizeMessageBatch(list = []) {
  const messageList = [];

  list.forEach((msg) => {
    const incomingMsg = normalizeIncomingMessage(msg);
    if (incomingMsg) {
      messageList.push(incomingMsg);
    }

    const responseMsg = normalizeResponseMessage(msg);
    if (responseMsg) {
      messageList.push(responseMsg);
    }
  });

  return messageList.sort((a, b) => {
    return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
  });
}

function mergeOlderMessages(currentList = [], olderList = []) {
  const repeatIdSet = new Set();

  currentList.forEach((currentMsg) => {
    olderList.forEach((olderMsg) => {
      if (currentMsg.id == olderMsg.id) {
        repeatIdSet.add(currentMsg.id);
      }
    });
  });

  const nextList = [...currentList];
  olderList.forEach((msg) => {
    if (!repeatIdSet.has(msg.id)) {
      nextList.unshift(msg);
    }
  });

  return nextList.sort((a, b) => {
    return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
  });
}

function scrollChatToBottom() {
  const el = document.querySelector(".chatContent");
  if (!el) return;

  setTimeout(() => {
    el.scrollTop = el.scrollHeight;
  }, 100);
}

function restorePrependScroll(previousHeight) {
  const el = document.querySelector(".chatContent");
  if (!el) return;

  setTimeout(() => {
    el.scrollTop = el.scrollHeight - previousHeight;
  });
}

function getChatHeight() {
  const el = document.querySelector(".chatContent");
  return el ? el.scrollHeight : 0;
}

export default {
  namespaced: true,

  state: {
    message: "",
    currentUserId: "",
    currentUser: "尚未選擇",
    messageList: [],
    isChatOpen: false,
    // 多久一次
    getTime: 30000,
    timeOut: "",
    messagePage: {},
    newMessagesCount: 0,
  },

  mutations: {
    SHOW(state, method) {
      state.isChatOpen = method;
    },

    RESET_MESSAGE_LIST(state) {
      state.messageList = [];
    },

    SET_MESSAGE_PAGE(state, pageData) {
      state.messagePage = pageData;
    },

    SET_MESSAGE_LIST(state, list) {
      state.messageList = list;
    },

    PREPEND_MESSAGE_LIST(state, list) {
      state.messageList = list;
    },

    PUSHMESSAGE(state, msg) {
      if (!state.messageList.length && !state.isChatOpen) return;

      const normalized = normalizeIncomingMessage(msg);
      if (!normalized) return;

      state.messageList = [...state.messageList, normalized].sort((a, b) => {
        return (
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );
      });
    },

    SETUNREAD(state, num) {
      if (state.isChatOpen) {
        state.newMessagesCount = 0;
        return;
      }
      state.newMessagesCount = num;
    },

    APPLY_REPLY_CONTENT(state, payload) {
      const { replyId, replyContent } = payload;

      state.messageList = state.messageList
        .map((item) => {
          if (item.reply_id == replyId && !item.replyContent) {
            return {
              ...item,
              replyContent,
            };
          }
          return item;
        })
        .sort((a, b) => a.id - b.id);
    },
  },

  actions: {
    showMessageBox({ commit, dispatch }, method) {
      commit("SHOW", method);
      if (method) {
        dispatch("getUserMessage");
      }
    },

    async getUserMessage({ commit, dispatch }, page) {
      const currentPage = page || 1;

      if (currentPage == 1 || !page) {
        commit("RESET_MESSAGE_LIST");
      }

      const res = await axios.get(`/mobile/chat?page=${currentPage}`);

      if (res.data.code == 0) {
        commit("SET_MESSAGE_PAGE", res.data);

        const normalizedList = normalizeMessageBatch(res.data.data);
        commit("SET_MESSAGE_LIST", normalizedList);

        await dispatch("setReply");
        scrollChatToBottom();
      }
    },

    async getPrePageMessage({ state, commit, dispatch }, page) {
      const currentHeight = getChatHeight();
      const res = await axios.get(`/mobile/chat?page=${page}`);

      if (res.data.code == 0) {
        commit("SET_MESSAGE_PAGE", res.data);

        const normalizedList = normalizeMessageBatch(res.data.data);
        const mergedList = mergeOlderMessages(
          state.messageList,
          normalizedList
        );

        commit("PREPEND_MESSAGE_LIST", mergedList);
        await dispatch("setReply");
        restorePrependScroll(currentHeight);
      }
    },

    async pushMsg({ commit, dispatch }, msg) {
      commit("PUSHMESSAGE", msg);
      await dispatch("setReply");
      scrollChatToBottom();
    },

    setUnreadNum({ commit }, num) {
      commit("SETUNREAD", num);
    },

    async setReply({ state, commit }) {
      const needReplyList = state.messageList.filter((item) => {
        return item.reply_id && !item.replyContent;
      });

      if (!needReplyList.length) return;

      const uniqueReplyIds = [
        ...new Set(needReplyList.map((item) => item.reply_id)),
      ];

      await Promise.all(
        uniqueReplyIds.map(async (replyId) => {
          try {
            const res = await axios.get(`/mobile/messages/${replyId}`);
            if (res?.data?.data) {
              commit("APPLY_REPLY_CONTENT", {
                replyId,
                replyContent: {
                  from: res.data.data.from == "会员" ? "自己:" : "客服:",
                  msg: res.data.data.message,
                },
              });
            }
          } catch (e) {
            // 保留原邏輯：失敗就略過，不阻斷主流程
            console.error("setReply error:", e);
          }
        })
      );
    },
  },
};
